import { motion } from "framer-motion";
import { LandingIcon } from "@/components/landing/icons";
import LeadForm from "@/components/landing/LeadForm";
import {
  Check, ArrowRight, ArrowDown, Quote, Sparkles, X as XIcon,
} from "lucide-react";
import { trackFunnel } from "@/lib/track";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

export function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`relative px-5 sm:px-8 py-16 sm:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary mb-3">
      <span className="h-px w-6 bg-primary/60" /> {children}
    </div>
  );
}

function Heading({ children }) {
  return <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">{children}</h2>;
}

/* ---------------- HERO ---------------- */
export function Hero({ c, onCta, hideForm }) {
  const h = c.hero;
  return (
    <section id="hero" className="relative px-5 sm:px-8 pt-10 sm:pt-16 pb-14">
      <div className={`max-w-6xl mx-auto grid ${hideForm ? "" : "lg:grid-cols-2"} gap-10 lg:gap-14 items-center`}>
        <motion.div {...reveal}>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> {h.badge}
          </div>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.1] tracking-tight">
            {h.headline}
          </h1>
          <p className="mt-4 text-lg text-slate-300 font-medium">{h.subheadline}</p>
          <p className="mt-3 text-slate-400 leading-relaxed max-w-xl">{h.supporting}</p>

          <div className="mt-6 grid grid-cols-3 gap-3 max-w-lg">
            {h.stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-3">
                <div className="font-heading text-xl sm:text-2xl font-extrabold text-primary">{s.value}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>

          <ul className="mt-6 space-y-2.5">
            {h.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-slate-200">
                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-primary/15 flex items-center justify-center">
                  <Check className="h-3 w-3 text-primary" />
                </span>
                <span className="text-[15px]">{b}</span>
              </li>
            ))}
          </ul>

          <button onClick={onCta} data-testid="hero-cta"
            className="mt-7 hidden lg:inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground font-bold px-6 py-3.5 hover:bg-primary/90 transition-colors">
            {h.cta} <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>

        {/* Form */}
        {!hideForm && (
        <motion.div id="lead-form" {...reveal} transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl border border-slate-700/70 bg-slate-900/80 backdrop-blur-md shadow-2xl p-6 sm:p-7">
          <div className="mb-5">
            <h3 className="font-heading text-xl font-bold">{h.form_heading}</h3>
            <p className="text-sm text-slate-400 mt-1">{h.form_subtext}</p>
          </div>
          <LeadForm content={h} cta={h.cta} testidPrefix="hero-lead" />
        </motion.div>
        )}
      </div>
    </section>
  );
}

/* ---------------- OPPORTUNITY / ECOSYSTEM ---------------- */
export function Opportunity({ c }) {
  const o = c.opportunity;
  return (
    <Section id="ecosystem" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{o.eyebrow}</Eyebrow>
        <Heading>{o.headline}</Heading>
        <p className="mt-4 text-slate-400 leading-relaxed">{o.description}</p>
      </motion.div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {o.items.map((it, i) => (
          <motion.div key={it.title} {...reveal} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
            className="group flex items-center gap-3.5 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-4 hover:border-primary/40 hover:bg-slate-900/70 transition-colors">
            <span className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <LandingIcon name={it.icon} className="h-5 w-5 text-primary" />
            </span>
            <span className="font-medium text-[15px]">{it.title}</span>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- PILLARS (alternating) ---------------- */
export function Pillars({ c }) {
  return (
    <>
      {c.pillars.map((p, i) => (
        <Section key={p.id} id={p.id} className={i % 2 ? "" : "bg-slate-900/30 border-y border-slate-800/70"}>
          <div className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? "" : "lg:[direction:rtl]"}`}>
            <motion.div {...reveal} className="lg:[direction:ltr]">
              <Eyebrow>{p.eyebrow}</Eyebrow>
              <Heading>{p.title}</Heading>
              <p className="mt-4 text-slate-400 leading-relaxed">{p.description}</p>
              <ul className="mt-6 space-y-2.5">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-slate-200">
                    <Check className="h-4 w-4 text-primary mt-1 shrink-0" /><span className="text-[15px]">{pt}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.1 }} className="lg:[direction:ltr]">
              <div className="relative rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-10 min-h-[240px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(400px circle at 30% 30%, rgba(0,230,153,0.18), transparent 60%)" }} />
                <LandingIcon name={p.icon} className="h-24 w-24 text-primary/80 relative" />
              </div>
            </motion.div>
          </div>
        </Section>
      ))}
    </>
  );
}

/* ---------------- PRODUCTS ---------------- */
export function Products({ c }) {
  const p = c.products;
  const cats = [...(p.categories || [])].filter((x) => x.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <Section id="products" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{p.eyebrow}</Eyebrow>
        <Heading>{p.headline}</Heading>
        <p className="mt-4 text-slate-400 leading-relaxed">{p.description}</p>
      </motion.div>
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {cats.map((cat, i) => (
          <motion.div key={cat.name} {...reveal} transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
            className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-primary/40 transition-colors">
            <div className="flex items-center justify-between">
              <span className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <LandingIcon name={cat.icon} className="h-4.5 w-4.5 text-primary" />
              </span>
              {cat.count && <span className="font-mono text-xs font-semibold text-primary">{cat.count}</span>}
            </div>
            <div className="mt-3 font-semibold text-[15px] leading-tight">{cat.name}</div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- AGENT NETWORK ---------------- */
export function AgentNetwork({ c }) {
  const a = c.agent_network;
  return (
    <Section id="agents" className="bg-slate-900/30 border-y border-slate-800/70">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...reveal}>
          <Eyebrow>{a.eyebrow}</Eyebrow>
          <Heading>{a.headline}</Heading>
          <p className="mt-4 text-slate-400 leading-relaxed">{a.description}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
            {a.points.map((pt) => (
              <div key={pt} className="flex items-center gap-2.5 rounded-lg border border-slate-800 bg-slate-950/40 px-3.5 py-2.5">
                <Check className="h-4 w-4 text-primary shrink-0" /><span className="text-sm">{pt}</span>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.1 }} className="relative min-h-[280px] flex items-center justify-center">
          <div className="relative h-16 w-16 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center z-10">
            <LandingIcon name="landmark" className="h-7 w-7 text-primary" />
          </div>
          {[0, 1, 2, 3, 4, 5].map((n) => {
            const angle = (n / 6) * Math.PI * 2;
            const x = Math.cos(angle) * 110, y = Math.sin(angle) * 110;
            return (
              <div key={n} className="absolute h-11 w-11 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center"
                style={{ transform: `translate(${x}px, ${y}px)` }}>
                <LandingIcon name="user" className="h-4 w-4 text-slate-300" />
              </div>
            );
          })}
        </motion.div>
      </div>
    </Section>
  );
}

/* ---------------- AUTOMATION FLOW ---------------- */
export function Automation({ c }) {
  const a = c.automation;
  return (
    <Section id="automation" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{a.eyebrow}</Eyebrow>
        <Heading>{a.headline}</Heading>
        <p className="mt-4 text-slate-400 leading-relaxed">{a.description}</p>
      </motion.div>
      <div className="mt-10 flex flex-wrap items-center gap-2.5">
        {a.steps.map((s, i) => (
          <motion.div key={s} {...reveal} transition={{ duration: 0.35, delay: i * 0.05 }} className="flex items-center gap-2.5">
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2.5 text-sm font-medium">
              <span className="font-mono text-xs text-primary mr-2">{String(i + 1).padStart(2, "0")}</span>{s}
            </div>
            {i < a.steps.length - 1 && <ArrowRight className="h-4 w-4 text-slate-600" />}
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- SELF FUNDING ---------------- */
export function SelfFunding({ c }) {
  const s = c.self_funding;
  return (
    <Section id="self-funding" className="bg-slate-900/30 border-y border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{s.eyebrow}</Eyebrow>
        <Heading>{s.headline}</Heading>
        <p className="mt-4 text-slate-400 leading-relaxed">{s.description}</p>
      </motion.div>
      <div className="mt-8 flex flex-wrap gap-2.5">
        {s.features.map((f, i) => (
          <motion.span key={f} {...reveal} transition={{ duration: 0.3, delay: (i % 6) * 0.04 }}
            className="rounded-lg border border-slate-800 bg-slate-950/50 px-3.5 py-2 text-sm text-slate-200">
            {f}
          </motion.span>
        ))}
      </div>
      {s.disclaimer && <p className="mt-6 text-xs text-slate-500 max-w-2xl">{s.disclaimer}</p>}
    </Section>
  );
}

/* ---------------- COMPARISON ---------------- */
export function Comparison({ c }) {
  const cmp = c.comparison;
  return (
    <Section id="comparison" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{cmp.eyebrow}</Eyebrow>
        <Heading>{cmp.headline}</Heading>
      </motion.div>
      <motion.div {...reveal} className="mt-8 overflow-hidden rounded-2xl border border-slate-800">
        <div className="grid grid-cols-2">
          <div className="bg-slate-900/60 px-4 sm:px-6 py-4 font-heading font-bold text-slate-300 border-b border-slate-800">{cmp.left_title}</div>
          <div className="bg-primary/10 px-4 sm:px-6 py-4 font-heading font-bold text-primary border-b border-slate-800">{cmp.right_title}</div>
          {cmp.rows.map((row, i) => (
            <div key={i} className="contents">
              <div className="flex items-center gap-2 px-4 sm:px-6 py-3.5 border-b border-slate-800/60 text-sm text-slate-400">
                <XIcon className="h-3.5 w-3.5 text-red-400/70 shrink-0" /> {row[0]}
              </div>
              <div className="flex items-center gap-2 px-4 sm:px-6 py-3.5 border-b border-slate-800/60 bg-primary/[0.04] text-sm text-slate-100 font-medium">
                <Check className="h-3.5 w-3.5 text-primary shrink-0" /> {row[1]}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
      {cmp.note && <p className="mt-4 text-xs text-slate-500">{cmp.note}</p>}
    </Section>
  );
}

/* ---------------- FOUNDER ---------------- */
export function Founder({ c }) {
  const f = c.founder;
  return (
    <Section id="founder" className="bg-slate-900/30 border-y border-slate-800/70">
      <div className="grid lg:grid-cols-[280px_1fr] gap-10 items-center">
        <motion.div {...reveal} className="mx-auto lg:mx-0">
          <div className="h-56 w-56 rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center">
            {f.photo ? <img src={f.photo} alt={f.name} className="h-full w-full object-cover" loading="lazy" />
              : <span className="font-heading text-6xl font-extrabold text-primary/60">{(f.name || "D")[0]}</span>}
          </div>
        </motion.div>
        <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.1 }}>
          <Eyebrow>{f.eyebrow}</Eyebrow>
          <h2 className="font-heading text-3xl font-extrabold">{f.name}</h2>
          <div className="text-primary font-medium mt-1">{f.designation}</div>
          <p className="mt-4 text-slate-400 leading-relaxed">{f.vision}</p>
          <blockquote className="mt-5 relative rounded-xl border border-slate-800 bg-slate-950/50 p-5 pl-12 text-slate-200 italic">
            <Quote className="absolute left-4 top-4 h-5 w-5 text-primary/60" />
            {f.quote}
          </blockquote>
        </motion.div>
      </div>
    </Section>
  );
}

/* ---------------- WHAT YOU GET ---------------- */
export function WhatYouGet({ c }) {
  const w = c.what_you_get;
  return (
    <Section id="what-you-get" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{w.eyebrow}</Eyebrow>
        <Heading>{w.headline}</Heading>
      </motion.div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {w.groups.map((g, i) => (
          <motion.div key={g.title} {...reveal} transition={{ duration: 0.4, delay: i * 0.06 }}
            className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
            <h4 className="font-heading font-bold text-primary mb-3">{g.title}</h4>
            <ul className="space-y-2.5">
              {g.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{it}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- WHO CAN START ---------------- */
export function WhoCanStart({ c }) {
  const w = c.who_can_start;
  return (
    <Section id="who" className="bg-slate-900/30 border-y border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{w.eyebrow}</Eyebrow>
        <Heading>{w.headline}</Heading>
      </motion.div>
      <div className="mt-8 flex flex-wrap gap-3">
        {w.items.map((it, i) => (
          <motion.div key={it} {...reveal} transition={{ duration: 0.3, delay: (i % 4) * 0.05 }}
            className="rounded-full border border-slate-700 bg-slate-950/50 px-5 py-2.5 text-sm font-medium text-slate-200 hover:border-primary/50 hover:text-primary transition-colors">
            {it}
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- VIDEO ---------------- */
function ytEmbed(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : "";
}

export function VideoSection({ c }) {
  const v = c.video;
  if (!v?.enabled || !v.url) return null;
  const embed = ytEmbed(v.url);
  return (
    <Section id="video" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Heading>{v.title}</Heading>
        {v.description && <p className="mt-3 text-slate-400">{v.description}</p>}
      </motion.div>
      <motion.div {...reveal} className="mt-8 rounded-2xl overflow-hidden border border-slate-800 bg-black aspect-video">
        {embed
          ? <iframe src={embed} title={v.title || "Video"} className="h-full w-full" frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
              onLoad={() => trackFunnel("VIDEO_PLAYED")} />
          : <video src={v.url} poster={v.thumbnail || undefined} controls playsInline
              onPlay={() => trackFunnel("VIDEO_PLAYED")} className="h-full w-full object-cover" />}
      </motion.div>
    </Section>
  );
}

/* ---------------- OFFICE ---------------- */
export function OfficeSection({ c }) {
  const o = c.office;
  if (!o?.enabled || !(o.images && o.images.length)) return null;
  return (
    <Section id="office" className="bg-slate-900/30 border-y border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Heading>{o.heading}</Heading>
        {o.description && <p className="mt-3 text-slate-400">{o.description}</p>}
      </motion.div>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {o.images.map((img, i) => (
          <div key={i} className="rounded-xl overflow-hidden border border-slate-800 aspect-[4/3]">
            <img src={typeof img === "string" ? img : img.url} alt={o.heading} loading="lazy" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- DOCUMENTS (inline PDF preview) ---------------- */
export function DocumentsSection({ c }) {
  const d = c.documents;
  if (!d?.enabled || !(d.items && d.items.length)) return null;
  return (
    <Section id="documents" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Heading>{d.heading}</Heading>
        {d.description && <p className="mt-3 text-slate-400">{d.description}</p>}
      </motion.div>
      <div className="mt-8 space-y-8">
        {d.items.map((doc, i) => (
          <motion.div key={i} {...reveal} className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="font-semibold text-sm">{doc.name || `Document ${i + 1}`}</span>
              <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs text-primary">Open</a>
            </div>
            <iframe src={doc.url} title={doc.name || `Document ${i + 1}`} className="w-full bg-white" style={{ height: "72vh" }} />
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- FAQ ---------------- */
export function Faq({ c }) {
  const f = c.faq;
  return (
    <Section id="faq" className="border-t border-slate-800/70">
      <motion.div {...reveal} className="max-w-3xl">
        <Eyebrow>{f.eyebrow}</Eyebrow>
        <Heading>{f.headline}</Heading>
      </motion.div>
      <div className="mt-8 max-w-3xl space-y-2.5">
        {f.items.map((item, i) => (
          <details key={i} className="group rounded-xl border border-slate-800 bg-slate-900/40 open:bg-slate-900/70 transition-colors"
            onToggle={(e) => e.target.open && trackFunnel("FAQ_INTERACTION", { q: item.q })} data-testid={`faq-item-${i}`}>
            <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between font-medium text-[15px]">
              {item.q}
              <ArrowDown className="h-4 w-4 text-slate-500 group-open:rotate-180 transition-transform shrink-0 ml-3" />
            </summary>
            <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{item.a}</div>
          </details>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- FINAL CTA ---------------- */
export function FinalCta({ c, hideForm }) {
  const f = c.final_cta;
  return (
    <Section id="final-cta" className="border-t border-slate-800/70">
      <div className="rounded-3xl border border-primary/20 bg-gradient-to-br from-slate-900 to-slate-950 p-7 sm:p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 80% 0%, rgba(0,230,153,0.12), transparent 55%)" }} />
        <div className={`relative grid ${hideForm ? "" : "lg:grid-cols-2"} gap-10 items-center`}>
          <motion.div {...reveal}>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{f.headline}</h2>
            <ul className="mt-6 space-y-2">
              {f.lines.map((l) => (
                <li key={l} className="flex items-center gap-2.5 text-lg text-slate-200 font-medium">
                  <Check className="h-5 w-5 text-primary shrink-0" />{l}
                </li>
              ))}
            </ul>
          </motion.div>
          {!hideForm && (
          <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-2xl border border-slate-700/70 bg-slate-900/80 backdrop-blur-md p-6 sm:p-7 shadow-2xl">
            <h3 className="font-heading text-xl font-bold mb-4">{f.cta}</h3>
            <LeadForm content={{ form_subtext: f.subtext, cta: f.cta }} cta={f.cta} testidPrefix="final-lead" />
          </motion.div>
          )}
        </div>
      </div>
    </Section>
  );
}
